import { useEffect, useMemo, useRef, useState } from "react";
import Icon from "../Icon";
import { useApi } from "../../hooks/useApi";
import { myAttendanceToday, punchIn, punchOut } from "../../api/attendanceService";
import { statusMeta, minutesLabel, captureLocation } from "../../utils/attendance";
import { formatDateTime, minutesSince } from "../../utils/formatters";
import { showToast, showApiError } from "../../utils/toast";

/**
 * Top-bar attendance indicator: a clock button showing check-in status at a
 * glance (green dot = currently checked in) plus an anchored popover with
 * today's shift, worked vs. expected time, shortfall/late, sessions and a
 * one-click Check in / Check out. Self-contained — owns its own 60s poll and
 * punch handling. Only mounted when the firm has the `attendance` addon.
 */
export default function AttendanceWidget() {
  const [open, setOpen] = useState(false);
  const [punching, setPunching] = useState(false);
  const [, setTick] = useState(0); // forces a re-render so the live figure moves
  const wrapRef = useRef(null);

  const today = useApi(() => myAttendanceToday());
  const t = today.data?.data;
  const checkedIn = !!t?.checked_in;

  // At-a-glance state for the top-bar clock icon, once today's status loads:
  //   checked in     -> green (all good)
  //   not checked in -> red "Not checked in" pill (a clear nudge to punch in)
  const needsCheckIn = !!t && !checkedIn;
  const statusColor = checkedIn
    ? "var(--success)"
    : needsCheckIn
      ? "var(--danger)"
      : undefined;
  const statusTitle = checkedIn
    ? "Checked in"
    : needsCheckIn
      ? "Not checked in — tap to check in"
      : "Attendance";

  // Keep the status dot fresh even while the popover is closed.
  useEffect(() => {
    const id = setInterval(() => { today.reload().catch(() => {}); }, 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [today.reload]);

  // Close on outside click / Esc; refresh on open. Mirrors SearchableSelect.
  useEffect(() => {
    if (!open) return;
    today.reload().catch(() => {});
    const onDocClick = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // While open and checked in, tick every 30s so "Worked" keeps climbing.
  useEffect(() => {
    if (!open || !checkedIn) return;
    const id = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [open, checkedIn]);

  const openLog = useMemo(
    () => (t?.logs || []).find((l) => !l.check_out_at) || null,
    [t],
  );
  // Completed slots + the running open slot, so the figure agrees with the muster.
  const liveWorked =
    (t?.summary?.worked_minutes || 0) +
    (checkedIn && openLog ? minutesSince(openLog.check_in_at) : 0);

  const doPunch = async () => {
    setPunching(true);
    try {
      const coords = await captureLocation();
      if (checkedIn) {
        await punchOut(coords);
        showToast("success", "Checked out");
      } else {
        await punchIn(coords);
        showToast("success", "Checked in");
      }
      await today.reload();
    } catch (err) {
      showApiError(err);
    } finally {
      setPunching(false);
    }
  };

  const meta = statusMeta(t?.summary?.status);
  const showBadge = meta && t?.summary?.status !== "absent";
  const shiftLabel = t?.shift
    ? `${t.shift.name} · ${String(t.shift.start_time).slice(0, 5)}–${String(t.shift.end_time).slice(0, 5)}`
    : "No shift assigned";
  const timeOnly = (ts) => formatDateTime(ts).split("·")[1]?.trim() || "…";

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-flex" }}>
      <button
        className={`icon-btn${needsCheckIn ? " attn-warn" : ""}`}
        title={statusTitle}
        aria-label={statusTitle}
        style={statusColor ? { color: statusColor } : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="clock" size={16} />
        {needsCheckIn && <span className="attn-warn-label">Not checked in</span>}
        {(checkedIn || needsCheckIn) && (
          <span className="dot" style={{ background: statusColor }} />
        )}
      </button>

      {open && (
        <div className="attn-pop">
          {!t ? (
            <div style={{ fontSize: 12, color: "var(--ink-500)", padding: "6px 0" }}>
              {today.loading ? "Loading…" : "Couldn't load attendance."}
            </div>
          ) : (
            <>
              <div style={S.head}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ ...S.statusDot, background: checkedIn ? "var(--success)" : needsCheckIn ? "var(--danger)" : "var(--ink-300, #cbd5e1)" }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-900)" }}>
                      {checkedIn ? "Checked in" : "Not checked in"}
                    </div>
                    {checkedIn && openLog && (
                      <div style={{ fontSize: 11, color: "var(--ink-500)" }}>
                        since {timeOnly(openLog.check_in_at)}
                      </div>
                    )}
                  </div>
                </div>
                {showBadge && (
                  <span style={{ ...S.badge, color: meta.color, background: meta.soft }}>{meta.label}</span>
                )}
              </div>

              <div style={S.rows}>
                <Row label="Shift" value={shiftLabel} />
                <Row
                  label="Worked"
                  value={
                    t.is_working_day
                      ? `${minutesLabel(liveWorked)} / ${minutesLabel(t.expected_minutes)}`
                      : minutesLabel(liveWorked)
                  }
                />
                {t.is_working_day && t.summary?.shortfall_minutes > 0 && (
                  <Row label="Shortfall" value={minutesLabel(t.summary.shortfall_minutes)} danger />
                )}
                {t.summary?.late && <Row label="Late" value={`${t.summary.late_minutes}m`} danger />}
              </div>

              {(t.logs || []).length > 0 && (
                <div style={S.sessions}>
                  <div style={S.sessionsHead}>Today's sessions</div>
                  {t.logs.map((l) => (
                    <div key={l.id} style={S.sessionRow}>
                      <Icon name="clock" size={11} />
                      <span>{timeOnly(l.check_in_at)}</span>
                      <span style={{ color: "var(--ink-400)" }}>→</span>
                      <span>{l.check_out_at ? timeOnly(l.check_out_at) : "…"}</span>
                    </div>
                  ))}
                </div>
              )}

              <button
                className={`btn ${checkedIn ? "btn-secondary" : "btn-primary"}`}
                style={{ width: "100%", marginTop: 12, justifyContent: "center" }}
                disabled={punching}
                onClick={doPunch}
              >
                <Icon name={checkedIn ? "log-out" : "check-circle"} size={14} />
                {punching ? "…" : checkedIn ? "Check out" : "Check in"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, danger }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, fontSize: 12 }}>
      <span style={{ color: "var(--ink-500)", flexShrink: 0 }}>{label}</span>
      {/* minWidth:0 lets the long "Shift · 09:00–18:00" value wrap instead of
          forcing the popover wider than the screen. */}
      <span
        style={{
          fontWeight: 500,
          color: danger ? "var(--danger, #dc2626)" : "var(--ink-900)",
          textAlign: "right",
          minWidth: 0,
          overflowWrap: "anywhere",
        }}
      >
        {value}
      </span>
    </div>
  );
}

// `.attn-pop` lives in index.css so it can be repositioned at the mobile
// breakpoint — the popover is anchored to this button, which sits to the LEFT
// of several other icon buttons, so `right: 0` pushes it off-screen on a phone.
const S = {
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 8,
    paddingBottom: 12,
    borderBottom: "1px solid var(--border)",
  },
  statusDot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  badge: { fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap" },
  rows: { display: "flex", flexDirection: "column", gap: 8, padding: "12px 0" },
  sessions: { borderTop: "1px solid var(--border)", paddingTop: 10, display: "flex", flexDirection: "column", gap: 5 },
  sessionsHead: { fontSize: 11, fontWeight: 600, color: "var(--ink-500)", marginBottom: 2 },
  sessionRow: { display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--ink-700, #334155)" },
};
